import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import moment from "moment";
import { Link } from "react-router-dom";
import { ListLayout } from "components/common/ListLayout";
import TableHeaderItem from "components/common/ListLayout/TableHeaderItem";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { PAGELENGTH } from "components/common/ListLayout";
import {
  appointmentRequestSelector,
  fetchAppointmentRequests,
} from "slices/appointmentRequestSlice";
import { getEyeStyle, getBackgroundStyle, LegendButton } from "components/common/TableColor";
import AddAppointment from "components/appointment/AddAppointment";
import CancelModal from 'components/appointment/CancelModal';
import Api from "services/api";
import toast from "react-hot-toast";
import { useNavigate } from 'react-router-dom';
import { Select, selectCustomStyles } from "components/common/Select";
import { AsyncPaginate } from "react-select-async-paginate";
import { fetchLanguages, languageSelector } from "slices/languageSlice";
import { fetchInterpreters } from "slices/interpreterSlice";
import { useTranslation } from "react-i18next";

function AppointmentRequests() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    appointmentRequests,
    last_page: totalPages,
    loader,
    total,
    current_page,
    per_page: pageLength,
  } = useSelector(appointmentRequestSelector);
  const { language: languages } = useSelector(languageSelector);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    sortBy: "id",
    sortDirection: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [activeLegend, setActiveLegend] = useState(0);
  const [statusValue, setStatusValue] = useState("all");
  const [interpreterState, setInterpreterState] = useState([]);
  const [filters, setFilters] = useState({
    start_date: moment().startOf("month").format("YYYY-MM-DD"),
    end_date: moment().endOf("month").format("YYYY-MM-DD"),
    status: "all",
    interpreter: "",
    language_id: "",
    service: "",
  });
  const [pageLengthState, setPageLengthState] = useState(PAGELENGTH);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [popupType, setPopupType] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelTargetItem, setCancelTargetItem] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      start_date: moment().startOf("month").format("YYYY-MM-DD"),
      end_date: moment().endOf("month").format("YYYY-MM-DD"),
      interpreter: "",
      language_id: "",
      service: "",
    },
  });

  const statusLegends = [
    { id: 0, name: t("translation.all"), value: "all", color: "#3176A0" },
    { id: 1, name: t("translation.newRequest"), value: "request,New Request", color: "#FFB82A" },
    { id: 2, name: t("translation.statusCancelled"), value: "cancelled", color: "#DC2626" },
  ];

  const serviceOptions = [
    { value: "In Person", label: t("translation.serviceInPerson") },
    { value: "VRI", label: t("translation.serviceVri") },
    { value: "OPI Pre Scheduled", label: t("translation.serviceTelephonic") },
  ];

  const filteredLanguages = React.useMemo(
    () =>
      languages?.map((language) => ({
        value: language?.id,
        label: language?.name,
      })),
    [languages]
  );

  const filteredLanguagesLoadOptions = async (
    inputValue,
    loadedOptions,
    { page }
  ) => {
    const result = await dispatch(
      fetchLanguages(
        page,
        {
          pageLength: 20,
          sortBy: "id",
          sortDirection: "asc",
          search: inputValue,
        },
        true,
        true
      )
    );
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      hasMore: page !== result?.last_page,
      additional: {
        page: page + 1,
      },
    };
  };

  const filteredInterpreterLoadOptions = async (
    inputValue,
    loadedOptions,
    { page }
  ) => {
    const result = await dispatch(
      fetchInterpreters(
        page,
        {
          pageLength: 20,
          sortBy: "id",
          sortDirection: "asc",
          search: inputValue,
        },
        true,
        true
      )
    );

    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.user.name,
      })),
      hasMore: page !== result?.last_page,
      additional: { page: page + 1 },
    };
  };

  useEffect(() => {
    dispatch(
      fetchAppointmentRequests(1, {
        pageLength: PAGELENGTH,
        start_date: filters.start_date,
        end_date: filters.end_date,
        status: filters.status,
        sortBy: sortConfig.sortBy,
        sortDirection: sortConfig.sortDirection,
      })
    );
  }, []);

  const handleActiveLegend = (id, value) => {
    setActiveLegend(id);
    setStatusValue(value);
    setCurrentPage(1);

    const newFilters = {
      ...filters,
      status: value,
      pageLength: pageLengthState,
      sortBy: sortConfig.sortBy,
      sortDirection: sortConfig.sortDirection,
    };
    setFilters(newFilters);
    dispatch(fetchAppointmentRequests(1, newFilters));
  };

  const onSubmit = (data) => {
    const newFilters = {
      start_date: data.start_date || "",
      end_date: data.end_date || "",
      status: statusValue,
      interpreter: data.interpreter || "",
      language_id: data.language_id || "",
      service: data.service || "",
      pageLength: pageLengthState,
      sortBy: sortConfig.sortBy,
      sortDirection: sortConfig.sortDirection,
    };
    setFilters(newFilters);
    setCurrentPage(1);
    dispatch(fetchAppointmentRequests(1, newFilters));
  };

  const handleResetFields = () => {
    const defaultStartDate = moment().startOf("month").format("YYYY-MM-DD");
    const defaultEndDate = moment().endOf("month").format("YYYY-MM-DD");
    reset({
      start_date: defaultStartDate,
      end_date: defaultEndDate,
      interpreter: "",
      language_id: "",
      service: "",
    });
    setInterpreterState([]);
    setActiveLegend(0);
    setStatusValue("all");

    const newFilters = {
      start_date: defaultStartDate,
      end_date: defaultEndDate,
      status: "all",
      interpreter: "",
      language_id: "",
      service: "",
      pageLength: pageLengthState,
      sortBy: sortConfig.sortBy,
      sortDirection: sortConfig.sortDirection,
    };
    setFilters(newFilters);
    setCurrentPage(1);
    dispatch(fetchAppointmentRequests(1, newFilters));
  };

  // const handleOpenAppointmentForm = async (request) => {
  //   const result = await Api.getAppointmentRequest(request.id);
  //   const data = result?.data?.data;

  //   setSelectedRequest({
  //     ...data,
  //     id: data?.id,
  //     timezone_id: data?.appointment_details?.timezone_id,
  //   });
  //   setPopupType("from-request");
  //   setShowAppointmentModal(true);
  // };

  const handleOpenAppointmentForm = async (request) => {
    const result = await Api.getAppointmentRequest(request.id);
    const data = result?.data?.data;

    if (!data) {
      toast.error('Failed to load appointment request details.');
      return;
    }

    setSelectedRequest({
      ...data,
      appointment_details: data.request_details,
      appointment_custom_pricing: data.request_custom_pricing,
      timezone_id: data.request_details?.timezone_id,
    });
    setPopupType('from-request');
    setShowAppointmentModal(true);
  };
  
  const handleOpenCancel = (item) => {
    setCancelTargetItem(item);
    setCancelModal(true);
  };

  const handleCloseCancel = () => {
    setCancelModal(false);
    setCancelTargetItem(null);
  };

  const handleCancelRequest = async (data) => {
    if (!cancelTargetItem) return;
    setCancelLoading(true);
    try {
      const response = await Api.cancelRequest(cancelTargetItem.id, data.reason);
      if (response?.status >= 200 && response?.status < 300) {
        toast.success('Appointment request cancelled successfully!');
        handleCloseCancel();
        dispatch(
          fetchAppointmentRequests(currentPage, {
            ...filters,
            pageLength: pageLengthState,
            sortBy: sortConfig.sortBy,
            sortDirection: sortConfig.sortDirection,
          })
        );
      } else if (response?.status === 422) {
        toast.error(response.data.message || 'Validation failed');
      } else {
        toast.error(response?.data?.message || 'Failed to cancel appointment request');
      }
    } catch (error) {
      console.error(error);
      toast.error('Error cancelling appointment request');
    } finally {
      setCancelLoading(false);
    }
  };
  const handleAppointmentModalClose = () => {
    setShowAppointmentModal(false);
    setSelectedRequest(null);
  };

  const handleAppointmentCreated = () => {
    setShowAppointmentModal(false);
    setSelectedRequest(null);
    dispatch(
      fetchAppointmentRequests(currentPage, {
        ...filters,
        pageLength: pageLengthState,
        sortBy: sortConfig.sortBy,
        sortDirection: sortConfig.sortDirection,
      })
    );
  };

  const handleMoveToLanguageRequest = async (requestId) => {
    setActionLoading(true);
    try {
      const result = await Api.moveToLanguageRequest(requestId);
      if (result?.status >= 200 && result?.status < 300) {
        toast.success(result.data?.message || 'Moved to language request');
        navigate('/language-requests');
        // dispatch(
        //   fetchAppointmentRequests(currentPage, {
        //     ...filters,
        //     pageLength: pageLengthState,
        //     sortBy: sortConfig.sortBy,
        //     sortDirection: sortConfig.sortDirection,
        //   })
        // );
      } else {
        toast.error(result?.data?.message || 'Unable to move request');
      }
    } catch (error) {
      toast.error('Unable to move request');
    }
    setActionLoading(false);
  };

  const renderFilters = () => {
    return (
      <div className="p-3 w-100">
        <div className="button-sm forLegends mb-3">
          <LegendButton
            activeId={activeLegend}
            options={statusLegends}
            handleClick={handleActiveLegend}
          />
        </div>
        <form className="search_form_ad">
          <div className="row-primary dahboard_filter" style={{ marginBottom: 10 }}>
            <div className="row-primary">
              <h2>{t("translation.startDate")}</h2>
              <Controller
                name="start_date"
                control={control}
                render={({ field }) => (
                  <div className="input-image basic_bases">
                    <input
                      type="date"
                      {...field}
                      value={field.value ? moment(field.value).format("YYYY-MM-DD") : ""}
                      onChange={(e) => {
                        const dateString = moment(new Date(e.target.value)).format("YYYY/MM/DD");
                        field.onChange(dateString);
                      }}
                    />
                    <img
                      src="/images/celender.png"
                      alt="calendar"
                      className="custom-icon"
                    />
                  </div>
                )}
              />
            </div>
            <div className="row-primary">
              <h2>{t("translation.endDate")}</h2>
              <Controller
                name="end_date"
                control={control}
                render={({ field }) => (
                  <div className="input-image basic_bases">
                    <input
                      type="date"
                      {...field}
                      value={field.value ? moment(field.value).format("YYYY-MM-DD") : ""}
                      onChange={(e) => {
                        const dateString = moment(new Date(e.target.value)).format("YYYY/MM/DD");
                        field.onChange(dateString);
                      }}
                    />
                    <img
                      src="/images/celender.png"
                      alt="calendar"
                      className="custom-icon"
                    />
                  </div>
                )}
              />
            </div>
            <div className="row-primary">
              <h2>{t("table.language")}</h2>
              <Controller
                name="language_id"
                control={control}
                render={({ field }) => (
                  <div className="basic_bases">
                    <AsyncPaginate
                      menuPlacement="auto"
                      styles={{ ...selectCustomStyles }}
                      value={
                        field.value
                          ? filteredLanguages?.find((opt) => field.value == opt.value)
                          : ""
                      }
                      loadOptions={filteredLanguagesLoadOptions}
                      isClearable
                      escapeClearsValue
                      onChange={(obj) => {
                        field.onChange(obj?.value || "");
                      }}
                      additional={{ page: 1 }}
                    />
                  </div>
                )}
              />
            </div>
            <div className="row-primary">
              <h2>{t("translation.serviceType")}</h2>
              <Controller
                name="service"
                control={control}
                render={({ field }) => (
                  <div className="basic_bases">
                    <Select
                      name="service"
                      options={serviceOptions}
                      {...field}
                      value={
                        field.value
                          ? serviceOptions?.find((opt) => field.value == opt.value)
                          : ""
                      }
                    />
                  </div>
                )}
              />
            </div>
          </div>
          <div className="row-primary btns_area row_10 mb-0">
            <SubmitButton
              handleSubmit={() => handleSubmit(onSubmit)()}
              buttonType="button"
              btnText={t("translation.filter")}
              className={"green"}
            />
            <SubmitButton
              buttonType="button"
              className={"lightBlue"}
              btnText={t("translation.reset")}
              handleSubmit={handleResetFields}
            />
          </div>
        </form>
      </div>
    );
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    dispatch(
      fetchAppointmentRequests(page, {
        ...filters,
        pageLength: pageLengthState,
        sortBy: sortConfig.sortBy,
        sortDirection: sortConfig.sortDirection,
      })
    );
  };

  const handlePageLengthChange = (length) => {
    setPageLengthState(length);
    dispatch(
      fetchAppointmentRequests(1, {
        ...filters,
        pageLength: length,
        sortBy: sortConfig.sortBy,
        sortDirection: sortConfig.sortDirection,
      })
    );
  };

  const renderTableHeader = React.useCallback(() => {
    const callBack = (dir, sortBy) => {
      setSortConfig({ sortBy, sortDirection: dir });
      dispatch(
        fetchAppointmentRequests(currentPage, {
          ...filters,
          pageLength: pageLengthState,
          sortBy,
          sortDirection: dir,
          search: searchTerm,
        })
      );
    };

    return (
        <tr>
            <th>#</th>
            <th>
                <TableHeaderItem
                    title={t("translation.appRequestId")}
                    sortBy="appid"
                    sortDirection={sortConfig.sortDirection}
                    callBack={callBack}
                />
            </th>

            <th>
                <TableHeaderItem
                    title={t("translation.status")}
                    sortBy="status"
                    sortDirection={sortConfig.sortDirection}
                    callBack={callBack}
                />
            </th>
            <th>
                <TableHeaderItem
                    title={t("table.service")}
                    sortBy="type"
                    sortDirection={sortConfig.sortDirection}
                    callBack={callBack}
                />
            </th>

            <th>
                <TableHeaderItem
                    title={t("table.date")}
                    sortBy="date"
                    sortDirection={sortConfig.sortDirection}
                    callBack={callBack}
                />
            </th>
            <th>
                <TableHeaderItem
                    title={t("table.language")}
                    sortBy="language_id"
                    sortDirection={sortConfig.sortDirection}
                    callBack={callBack}
                />
            </th>

            <th>
                <TableHeaderItem
                    title={t("table.client")}
                    sortBy="account_id"
                    sortDirection={sortConfig.sortDirection}
                    callBack={callBack}
                />
            </th>
            <th>
                <TableHeaderItem
                    title={t("table.subAccount")}
                    sortBy="subclient_id"
                    sortDirection={sortConfig.sortDirection}
                    callBack={callBack}
                />
            </th>
            {/* <th>Action</th> */}
        </tr>
    );
  }, [currentPage, filters, pageLengthState, searchTerm, sortConfig]);

  const renderTableContent = React.useCallback(() => {
    return (
      appointmentRequests?.map((item, index) => {
        const serial = index + 1 + (current_page - 1) * pageLength;
        let style = getBackgroundStyle(item?.status);
        return (
          <tr key={item?.id || index} style={style}>
            {/* <td style={style}>{serial}</td> */}
            <td className='p-0' style={style}>
                <div className={`eye_area  ${item?.appointment_details?.extra_mileage_request == 1 && 'extra-mileage'}`} style={getEyeStyle(item?.status, style)}>
                    <Link to={`/appointment-requests/view/${item?.id}`}><i class="fa-solid fa-eye"></i></Link>
                </div>
            </td>
            <td style={style}>{item.appid || 'N/A'}</td>
            <td style={style}>{item?.status == 'cancelled' ? item?.request_details?.cancelled_by === 'mb' ? 'Cancelled by MB' : 'Cancelled' : item?.status}</td>
            <td style={style}>{item?.type || 'N/A'}</td>
            <td style={style}>{moment(item?.datetime, 'YYYY-MM-DD hh:mm A').format('MMM D, YYYY hh:mm A') || 'N/A'}</td>
            <td style={style}>{item?.language?.name ? item?.language?.name : item?.language_requests?.[0]?.language_name || 'N/A'}</td>
            <td style={style}>{item?.accounts?.user?.name || 'N/A'}</td>
            <td style={style}>{item?.subclient?.subaccount_name || 'N/A'}</td>
            {/* <td style={style}>
              <div className="row-primary" style={{ gap: 8 }}>
                {(item?.status === 'Request' || item?.status === 'New Request') && item?.status !== 'cancelled' && (
                  <button
                    type="button"
                    className="main_button green"
                    style={{ padding: '12px 14px' }}
                    onClick={() => handleOpenAppointmentForm(item)}
                    title="View Request & Convert"
                  >
                   <i className="fa-solid fa-eye"></i>
                  </button>
                )}

                {!item?.appointment_id && item?.status !== 'cancelled' && (
                  <button
                    type="button"
                    className="main_button tomatoRed"
                    style={{ padding: '12px 14px' }}
                    onClick={() => handleOpenCancel(item)}
                    title="Cancel Request"
                  >
                   <i className="fa-solid fa-circle-xmark"></i> 
                  </button>
                )}

                {item?.status !== 'approved' && item?.status !== 'cancelled' && item?.status !== 'Accepted' && (
                  <button
                    type="button"
                    className="main_button"
                    style={{ padding: '12px 14px' }}
                    onClick={() => handleMoveToLanguageRequest(item?.id)}
                    disabled={actionLoading}
                    title="Move To Language Request"
                  >
                    <i className="fa-solid fa-globe"></i> 
                  </button>
                )}
              </div>
            </td> */}
          </tr>
        );
      })
    );
  }, [appointmentRequests, current_page, pageLength, pageLengthState, filters, sortConfig, actionLoading, cancelLoading, cancelTargetItem]);

  return (
    <>
      <ListLayout
        heading={t("translation.appointmentRequests")}
        title=""
        renderTableHeader={renderTableHeader}
        renderTableContent={renderTableContent}
        renderFilters={renderFilters}
        totalPages={totalPages}
        fetchList={fetchAppointmentRequests}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        loader={loader}
        noOfItems={total}
        addButtonDisabled={true}
        hideHeader={false}
        disableBootstrap={true}
        searchPlaceholder={t("translation.searchAppointmentRequests")}
        hidePagination={false}
        pageLengthChange={handlePageLengthChange}
        paginationParams={filters}
        defaultPageLength={PAGELENGTH}
        sortConfig={sortConfig}
      />
      <AddAppointment
        item={selectedRequest}
        show={showAppointmentModal}
        onClose={handleAppointmentModalClose}
        callback={handleAppointmentCreated}
        popupType={popupType}
      />
      <CancelModal
        isOpen={cancelModal}
        onClose={handleCloseCancel}
        onConfirm={handleCancelRequest}
        appointmentId={cancelTargetItem?.appid || cancelTargetItem?.id}
        item={cancelTargetItem}
        loader={cancelLoading}
        heading="Request"
      />
    </>
  );
}

export default AppointmentRequests;
